import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  apiService,
  useStripeTopUpControllerCatalogQuery,
  useStripeBillingHistoryControllerDocumentQuery,
  useStripeBillingHistoryControllerReadQuery,
  type BillingSubscriptionInvoiceDto,
  type BillingTopUpPurchaseDto,
} from '@shared/services/apiService';

import { billingDate, formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

const columns = '100px minmax(0,1fr) 102px 104px 130px 150px';
const date = billingDate;
const money = (minor: string, currency: string, scale: number | null) =>
  scale === null
    ? 'Unavailable'
    : `${minor.startsWith('-') ? '−' : ''}${formatBillingMoney(minor.replace(/^-/, ''), currency, scale)}`;

export const billingInvoicePdfUrl = (value: string | null): string | undefined => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      ['pay.stripe.com', 'invoice.stripe.com'].includes(url.hostname) &&
      url.pathname.endsWith('/pdf')
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};

export const safePurchaseDocumentUrl = (value: string): string | undefined => {
  const invoice = billingInvoicePdfUrl(value);
  if (invoice) return invoice;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      url.hostname === 'pay.stripe.com' &&
      url.pathname.startsWith('/receipts/')
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};

const PurchaseDocumentButton = ({
  companyId,
  purchaseId,
}: {
  companyId: string;
  purchaseId: string;
}) => {
  const [load, query] = apiService.useLazyStripeBillingHistoryControllerDocumentQuery();
  const active = useRef(false);
  const pendingWindow = useRef<Window | null>(null);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      pendingWindow.current?.close();
    };
  }, []);
  const [fallback, setFallback] = useState<string | null>(null);
  const open = async () => {
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    pendingWindow.current = popup;
    try {
      const doc = await load({ companyId, purchaseId }, false).unwrap();
      const url = doc.url ? safePurchaseDocumentUrl(doc.url) : undefined;
      if (!active.current) {
        popup?.close();
        return;
      }
      if (url && popup) {
        popup.location.replace(url);
        pendingWindow.current = null;
      } else {
        popup?.close();
        setFallback(url ?? '');
      }
    } catch {
      popup?.close();
    }
  };
  return (
    <Box>
      {fallback ? (
        <Button
          size="small"
          component="a"
          href={fallback}
          target="_blank"
          rel="noopener noreferrer">
          Open document
        </Button>
      ) : (
        <Button size="small" disabled={query.isFetching} onClick={() => void open()}>
          Invoice / receipt
        </Button>
      )}
      {query.isError && (
        <Typography variant="caption" color="error">
          Document unavailable. Try again.
        </Typography>
      )}
      {fallback === '' && (
        <Typography variant="caption" color="text.secondary">
          Document not yet available.
        </Typography>
      )}
    </Box>
  );
};

const purchaseLabels: Record<string, string> = {
  prepared: 'Ready for checkout',
  sending: 'Preparing checkout',
  unknown: 'Checking payment',
  open: 'Awaiting payment',
  pending: 'Verifying payment',
  succeeded: 'Credits added',
  expired: 'Not charged',
  review: 'Needs review',
};
const invoiceLabels: Record<string, string> = {
  paid: 'Paid',
  open: 'Payment due',
  draft: 'Draft',
  void: 'Voided',
  uncollectible: 'Unpaid',
  unknown: 'Checking status',
};

export const BillingPaymentHistory = ({
  companyId,
  purchases,
  canResume,
  resuming,
  onResume,
  refreshSignal = 0,
}: {
  companyId: string;
  purchases: BillingTopUpPurchaseDto[];
  canResume: boolean;
  resuming: boolean;
  onResume: (purchase: BillingTopUpPurchaseDto) => void;
  refreshSignal?: number;
}) => {
  const [from, setFrom] = useState(''),
    [to, setTo] = useState('');
  const rangeValid = !from || !to || from <= to;
  const range = {
    ...(rangeValid && from ? { from: `${from}T00:00:00.000Z` } : {}),
    ...(rangeValid && to
      ? { to: new Date(Date.parse(`${to}T00:00:00Z`) + 86400000).toISOString() }
      : {}),
  };
  const query = useStripeBillingHistoryControllerReadQuery(
    { companyId, ...range },
    { refetchOnMountOrArgChange: true },
  );
  const topUps = useStripeTopUpControllerCatalogQuery(
    { companyId, ...range },
    { refetchOnMountOrArgChange: true },
  );
  const { refetch: refetchPurchases } = topUps;
  const [loadPurchases, purchasePage] = apiService.useLazyStripeTopUpControllerCatalogQuery();
  const [olderPurchases, setOlderPurchases] = useState<BillingTopUpPurchaseDto[]>([]);
  const [purchaseCursor, setPurchaseCursor] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState(false);
  const [selected, setSelected] = useState<BillingTopUpPurchaseDto | null>(null);
  const document = useStripeBillingHistoryControllerDocumentQuery(
    { companyId, purchaseId: selected?.id ?? '' },
    { skip: !selected || selected.state !== 'succeeded' },
  );
  const [loadPage, pageState] = apiService.useLazyStripeBillingHistoryControllerReadQuery();
  const { refetch } = query;
  const first = query.isError ? undefined : query.currentData;
  const [older, setOlder] = useState<BillingSubscriptionInvoiceDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pageError, setPageError] = useState(false);
  const [filter, setFilter] = useState<'all' | 'subscription' | 'topup'>('all');
  const epoch = useRef(0);
  const active = useRef(true);
  const previousRefresh = useRef(refreshSignal);
  useEffect(() => {
    active.current = true;
    const requestEpoch = epoch;
    return () => {
      active.current = false;
      requestEpoch.current++;
    };
  }, []);
  useEffect(() => {
    epoch.current++;
    setOlder([]);
    setCursor(first?.nextInvoiceCursor ?? null);
    setPageError(false);
  }, [first, companyId]);
  const firstPurchases = topUps.isError ? undefined : topUps.currentData;
  useEffect(() => {
    epoch.current++;
    setOlderPurchases([]);
    setPurchaseCursor(firstPurchases?.nextPurchaseCursor ?? null);
    setPurchaseError(false);
    setSelected(null);
  }, [firstPurchases, companyId, from, to]);
  useEffect(() => {
    if (previousRefresh.current !== refreshSignal) {
      previousRefresh.current = refreshSignal;
      void refetch();
      void refetchPurchases();
    }
  }, [refreshSignal, refetch, refetchPurchases]);
  const loadOlder = async () => {
    if (!cursor || query.isFetching || query.isError || pageState.isFetching) return;
    const generation = epoch.current;
    setPageError(false);
    try {
      const result = await loadPage({ companyId, after: cursor, ...range }, false).unwrap();
      if (!active.current || epoch.current !== generation) return;
      setOlder((rows) => [...rows, ...result.invoices]);
      setCursor(result.nextInvoiceCursor);
    } catch {
      if (active.current && epoch.current === generation) setPageError(true);
    }
  };
  const loadOlderPurchases = async () => {
    if (!purchaseCursor || topUps.isFetching || topUps.isError || purchasePage.isFetching) return;
    const generation = epoch.current;
    setPurchaseError(false);
    try {
      const result = await loadPurchases(
        { companyId, after: purchaseCursor, ...range },
        false,
      ).unwrap();
      if (!active.current || epoch.current !== generation) return;
      setOlderPurchases((rows) => [...rows, ...result.purchases]);
      setPurchaseCursor(result.nextPurchaseCursor ?? null);
    } catch {
      if (active.current && epoch.current === generation) setPurchaseError(true);
    }
  };
  const allPurchases = [
    ...new Map(
      [...(firstPurchases?.purchases ?? (!from && !to ? purchases : [])), ...olderPurchases].map(
        (p) => [p.id, p],
      ),
    ).values(),
  ];
  const invoices = first
    ? [...new Map([...first.invoices, ...older].map((i) => [i.id, i])).values()]
    : [];
  const rows = [
    ...allPurchases.map((purchase) => ({
      kind: 'topup' as const,
      id: purchase.id,
      createdAt: purchase.createdAt,
      title: purchase.title,
      detail: purchase.kind === 'automatic' ? 'Automatic top-up' : 'Manual top-up',
      period: null as string | null,
      number: null as string | null,
      credits:
        purchase.state === 'succeeded'
          ? `+${formatCreditUnits(purchase.units, purchase.creditScale)} credits`
          : 'No credits added',
      amount:
        purchase.state === 'expired'
          ? '—'
          : money(purchase.amountMinor, purchase.currency, purchase.currencyScale),
      status: purchaseLabels[purchase.state] ?? 'Checking status',
      success: purchase.state === 'succeeded',
      warning: ['review', 'pending', 'unknown', 'open'].includes(purchase.state),
      pdf: undefined as string | undefined,
      purchase,
    })),
    ...invoices.map((invoice) => ({
      kind: 'subscription' as const,
      id: invoice.id,
      createdAt: invoice.createdAt,
      title: invoice.description,
      detail: invoice.planName,
      number: invoice.number,
      period:
        invoice.startsAt && invoice.endsAt
          ? `${date(invoice.startsAt)} – ${date(invoice.endsAt)}`
          : null,
      credits:
        invoice.creditUnits !== null && invoice.creditScale !== null
          ? `+${formatCreditUnits(invoice.creditUnits, invoice.creditScale)} credits`
          : '—',
      amount: money(invoice.amountMinor, invoice.currency, invoice.currencyScale),
      status: invoiceLabels[invoice.status],
      success: invoice.status === 'paid',
      warning: ['open', 'uncollectible'].includes(invoice.status),
      pdf: billingInvoicePdfUrl(invoice.invoicePdfUrl),
      purchase: undefined,
    })),
  ].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const visible = rows.filter((row) => filter === 'all' || row.kind === filter);
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box>
          <Typography variant="h6" component="h2">
            Billing history
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Subscription invoices and credit purchases.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          aria-label="Filter billing history"
          onChange={(_event, value: typeof filter | null) => {
            if (value) setFilter(value);
          }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="subscription">Subscriptions</ToggleButton>
          <ToggleButton value="topup">Top-ups</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          size="small"
          label="From date"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          label="To date"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {(from || to) && (
          <Button
            onClick={() => {
              setFrom('');
              setTo('');
            }}>
            Clear dates
          </Button>
        )}
      </Stack>
      {!rangeValid && (
        <Alert severity="warning">The end date must be on or after the start date.</Alert>
      )}
      {topUps.isError && (
        <Alert
          severity="warning"
          action={<Button onClick={() => void topUps.refetch()}>Retry purchases</Button>}>
          Credit purchase history could not be updated.
        </Alert>
      )}
      {query.isLoading && <CircularProgress size={20} aria-label="Loading subscription invoices" />}
      {query.isError && (
        <Alert
          severity="warning"
          action={<Button onClick={() => void query.refetch()}>Retry invoices</Button>}>
          Subscription invoices could not be loaded. Credit purchases remain available below.
        </Alert>
      )}
      {!!visible.length && (
        <Box role="table" aria-label="Billing history">
          <Box
            role="row"
            sx={{
              display: { xs: 'none', lg: 'grid' },
              gridTemplateColumns: columns,
              columnGap: 1.5,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
            {['Date', 'Description', 'Credits', 'Amount', 'Status', 'Document'].map((label) => (
              <Typography key={label} role="columnheader" variant="caption" color="text.secondary">
                {label}
              </Typography>
            ))}
          </Box>
          <Box role="rowgroup">
            {visible.map((row) => (
              <Box
                role="row"
                key={`${row.kind}:${row.id}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr) auto', lg: columns },
                  columnGap: 1.5,
                  rowGap: 1,
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  '& > *': { minWidth: 0 },
                }}>
                <Typography
                  role="cell"
                  variant="body2"
                  color="text.secondary"
                  sx={{ gridColumn: { xs: 1, lg: 'auto' }, gridRow: { xs: 1, lg: 'auto' } }}>
                  {date(row.createdAt)}
                </Typography>
                <Box
                  role="cell"
                  sx={{
                    gridColumn: { xs: '1 / -1', lg: 'auto' },
                    gridRow: { xs: 2, lg: 'auto' },
                    overflowWrap: 'anywhere',
                  }}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {row.detail}
                  </Typography>
                  {row.period && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {row.period}
                    </Typography>
                  )}
                  {row.number && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {row.number}
                    </Typography>
                  )}
                </Box>
                <Typography
                  role="cell"
                  variant="body2"
                  sx={{
                    gridColumn: { xs: 1, lg: 'auto' },
                    gridRow: { xs: 3, lg: 'auto' },
                    fontVariantNumeric: 'tabular-nums',
                    overflowWrap: 'anywhere',
                  }}>
                  {row.credits}
                </Typography>
                <Typography
                  role="cell"
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    gridColumn: { xs: 2, lg: 'auto' },
                    gridRow: { xs: 1, lg: 'auto' },
                    textAlign: { xs: 'right', lg: 'left' },
                    fontVariantNumeric: 'tabular-nums',
                    overflowWrap: 'anywhere',
                  }}>
                  {row.amount}
                </Typography>
                <Box
                  role="cell"
                  sx={{
                    gridColumn: { xs: 2, lg: 'auto' },
                    gridRow: { xs: 3, lg: 'auto' },
                    textAlign: { xs: 'right', lg: 'left' },
                  }}>
                  <Chip
                    size="small"
                    label={row.status}
                    color={row.success ? 'success' : row.warning ? 'warning' : 'default'}
                    sx={(theme) => {
                      const status =
                        theme.palette.status[
                          row.success ? 'success' : row.warning ? 'warning' : 'neutral'
                        ];
                      return {
                        maxWidth: '100%',
                        color: status.foreground,
                        bgcolor: status.background,
                        border: '1px solid',
                        borderColor: status.border,
                      };
                    }}
                  />
                </Box>
                <Box
                  role="cell"
                  sx={{
                    gridColumn: { xs: '1 / -1', lg: 'auto' },
                    gridRow: { xs: 4, lg: 'auto' },
                    textAlign: { xs: 'right', lg: 'left' },
                  }}>
                  {row.purchase?.state === 'succeeded' && (
                    <PurchaseDocumentButton
                      key={`${companyId}:${row.id}`}
                      companyId={companyId}
                      purchaseId={row.purchase.id}
                    />
                  )}
                  {row.purchase && (
                    <Button size="small" onClick={() => setSelected(row.purchase!)}>
                      Details
                    </Button>
                  )}
                  {row.pdf && (
                    <Button
                      size="small"
                      component="a"
                      href={row.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Download invoice ${row.number ?? row.id}`}>
                      Invoice / receipt
                    </Button>
                  )}
                  {row.purchase &&
                    row.purchase.kind !== 'automatic' &&
                    ['prepared', 'open', 'unknown'].includes(row.purchase.state) && (
                      <Button
                        size="small"
                        disabled={resuming || !canResume}
                        onClick={() => onResume(row.purchase!)}>
                        Continue checkout
                      </Button>
                    )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {!visible.length && !query.isLoading && (
        <Typography variant="body2" color="text.secondary">
          {filter === 'topup'
            ? 'No credit purchases yet.'
            : filter === 'subscription'
              ? 'No subscription invoices in the loaded history.'
              : 'No billing activity in this date range.'}
        </Typography>
      )}
      {purchaseError && (
        <Alert severity="warning">
          Older purchases could not be loaded. Loaded entries remain available.
        </Alert>
      )}
      {purchaseCursor && filter !== 'subscription' && (
        <Button
          disabled={purchasePage.isFetching || topUps.isFetching}
          onClick={() => void loadOlderPurchases()}>
          {purchasePage.isFetching ? 'Loading older purchases…' : 'Load older purchases'}
        </Button>
      )}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="purchase-detail-title">
        <DialogTitle id="purchase-detail-title">Credit purchase details</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {selected && (
              <>
                <Typography>
                  {selected.title} · {purchaseLabels[selected.state] ?? 'Checking status'}
                </Typography>
                <Typography variant="body2">
                  Ordered: {formatCreditUnits(selected.units, selected.creditScale)} credits for{' '}
                  {money(selected.amountMinor, selected.currency, selected.currencyScale)}
                </Typography>
                <Typography variant="body2">
                  Credits added:{' '}
                  {selected.state === 'succeeded'
                    ? formatCreditUnits(selected.units, selected.creditScale)
                    : '0'}
                </Typography>
                <Typography variant="body2">
                  {selected.kind === 'automatic' ? 'Automatic top-up' : 'Manual top-up'} ·{' '}
                  {date(selected.createdAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}>
                  Reference: {selected.id}
                </Typography>
                {selected.state === 'expired' && (
                  <Alert severity="info">
                    Checkout expired without a confirmed payment. You were not charged and no
                    credits were added.
                  </Alert>
                )}
                {selected.state === 'succeeded' && (
                  <>
                    {document.isFetching && (
                      <CircularProgress size={20} aria-label="Loading purchase document" />
                    )}
                    {document.isError && (
                      <Alert
                        severity="warning"
                        action={
                          <Button onClick={() => void document.refetch()}>Retry document</Button>
                        }>
                        The payment document could not be loaded.
                      </Alert>
                    )}
                    {document.currentData?.url &&
                      safePurchaseDocumentUrl(document.currentData.url) && (
                        <Button
                          component="a"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={safePurchaseDocumentUrl(document.currentData.url)}>
                          Open {document.currentData.kind === 'invoice' ? 'invoice PDF' : 'receipt'}
                        </Button>
                      )}
                    {!document.isFetching &&
                      !document.isError &&
                      document.currentData &&
                      !document.currentData.url && (
                        <Typography variant="body2" color="text.secondary">
                          A payment document is not yet available from Stripe.
                        </Typography>
                      )}
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {pageError && (
        <Alert severity="warning">
          Older invoices could not be loaded. The current history is still available.
        </Alert>
      )}
      {first && cursor && filter !== 'topup' && (
        <Button
          variant="text"
          disabled={pageState.isFetching || query.isFetching}
          onClick={() => void loadOlder()}>
          {pageState.isFetching ? 'Loading older invoices…' : 'Load older invoices'}
        </Button>
      )}
    </Stack>
  );
};
